import { useTheme } from '@emotion/react';
import { Dialog } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import IconsButton from '@shared/components/core/button/IconsButton';
import { BodyMedium } from '@shared/components/core/text/Typography';
import { createUUID } from '@shared/constants/Keys';
import IContentDialogProps from '@shared/models/components/IContentDialogProps';
import { useTranslation } from 'react-i18next';
import {
    DialogTitleWrap,
    DisplayDialogTitle,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTag,
} from './CoreDialogStyles';

export default function ContentDialog({
    title,
    description,
    onCancel,
    cancelButtonText,
    onConfirm,
    onClose,
    confirmButtonText,
    confirmIcon,
    cancelIcon,
    children,
    scroll = 'paper',
    dialogTag,
    ...restProps
}: Readonly<IContentDialogProps>) {
    const { t } = useTranslation();
    const theme = useTheme();

    const randomId = createUUID();
    const cancelText = cancelButtonText ?? t('common:cancel');
    const confirmText = confirmButtonText ?? t('common:save');

    return (
        <Dialog
            sx={{
                '& .MuiPaper-root': {
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: theme.color.border,
                    background: theme.color.background,
                },
            }}
            scroll={scroll}
            onClose={onClose}
            aria-labelledby={`dialog-title-${randomId}`}
            aria-describedby={description ? `dialog-description-${randomId}` : undefined}
            {...restProps}>
            <DialogTitleWrap>
                <DisplayDialogTitle id={`dialog-title-${randomId}`}>
                    {dialogTag && <StyledDialogTag as="span">{dialogTag}</StyledDialogTag>}
                    {title}
                </DisplayDialogTitle>
                {onClose && <IconsButton label={'Close'} startIcon="closeX" $color="accent" onClick={onClose} />}
            </DialogTitleWrap>
            <StyledDialogContent>
                {description && <BodyMedium id={`dialog-description-${randomId}`}>{description}</BodyMedium>}
                {children}
            </StyledDialogContent>

            <StyledDialogActions>
                {cancelText && onCancel && (
                    <Button $size="small" $color="simple" startIcon={cancelIcon} onClick={onCancel}>
                        {cancelText}
                    </Button>
                )}
                <Button $size="small" $color="secondary" startIcon={confirmIcon} onClick={onConfirm}>
                    {confirmText}
                </Button>
            </StyledDialogActions>
        </Dialog>
    );
}
